// DispatchDeck — pay-period lock, CLIENT half (TASK-9443)
//
// SOFT lock (Chris 2026-09-29): once payroll has been exported for a period an
// edit to a clock entry is still ALLOWED, but (1) the editor is told, and
// (2) every hour that moved since the export is listed in Payroll Prep. Nothing
// here refuses an edit, and every failure path is fail-open — a lock lookup
// that errors must never trap a dispatcher.
//
// PURE + DOM-free: no Firebase, no AppState. clock.js imports it once for the
// side effect (`import '../payrollRunLock.js?v=…'`, sets window.PayrollRunLock)
// and injects the getPayrollRuns fetcher; payroll.js reads window.PayrollRunLock
// at call time (Pitfall #1 — never a second `?v=` import). Node tests require()
// it directly (UMD).
//
// The stamp itself lives on the server (functions/src/services/payrollRunLock.js,
// tenants/{tid}/payrollRuns/{startYmd_endYmd}); a run's `rows` is the snapshot
// of EXACTLY what was exported, which is what drift is measured against — so
// the list is correct whichever writer changed the hours.
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.PayrollRunLock = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this), function () {
  'use strict';

  const FIELDS = ['reg', 'ot', 'bonus', 'reimb'];
  const FIELD_LABEL = { reg: 'regular hours', ot: 'overtime hours', bonus: 'bonus', reimb: 'reimbursement' };
  const EPS = 0.005;
  const CACHE_MS = 60 * 1000;

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const pad = (n) => String(n).padStart(2, '0');
  function ymdOf(ms) {
    const d = new Date(ms);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  // What the Gusto file carries per person — SAME arithmetic as payroll.js
  // buildGustoRows (tests/task9443-pay-period-lock.unit.cjs proves parity).
  // Salaried = no hours; an all-zero row is not exported so it is not in the
  // snapshot either. CONTRACTORS are not in the file: the CALLER (payroll.js,
  // which owns the derived row gate) passes rows with them already removed —
  // this module never decides W-2 vs 1099 (TASK-9178).
  function snapshotRows(prepRows) {
    const out = [];
    (prepRows || []).forEach((r) => {
      if (!r) return;
      const reg = r.isSalary ? 0 : (r.reg || 0);
      const ot = r.isSalary ? 0 : (r.ot || 0);
      const bonus = (r.bonus || 0) + (r.vehicleTaxable || 0);
      const reimb = (r.perDiemDollars || 0) + (r.reimbursement || 0) + (r.vehicleReimb || 0);
      if (reg <= 0.004 && ot <= 0.004 && bonus <= 0.004 && reimb <= 0.004) return;
      out.push({ pid: String(r.pid || ''), name: String(r.name || ''),
        reg: round2(reg), ot: round2(ot), bonus: round2(bonus), reimb: round2(reimb) });
    });
    return out;
  }

  const keyOf = (r) => (r.pid ? 'p:' + r.pid : 'n:' + String(r.name || '').toLowerCase());

  // baseline (what was exported) vs current (same period recomputed now).
  // One change per person per field that moved; a person who appears or
  // vanishes shows every non-zero field against 0.
  function diffRows(baseline, current) {
    const base = new Map((baseline || []).map((r) => [keyOf(r), r]));
    const cur = new Map((current || []).map((r) => [keyOf(r), r]));
    const out = [];
    const keys = new Set([...base.keys(), ...cur.keys()]);
    keys.forEach((k) => {
      const b = base.get(k) || {};
      const c = cur.get(k) || {};
      FIELDS.forEach((f) => {
        const from = round2(b[f] || 0), to = round2(c[f] || 0);
        if (Math.abs(to - from) >= EPS) {
          out.push({ pid: (c.pid || b.pid || ''), name: (c.name || b.name || ''), field: f, from, to });
        }
      });
    });
    out.sort((x, y) => (x.name || '').localeCompare(y.name || '') || FIELDS.indexOf(x.field) - FIELDS.indexOf(y.field));
    return out;
  }

  const describe = (c) => `${c.name || 'Someone'}: ${FIELD_LABEL[c.field] || c.field} ${c.from.toFixed(2)} → ${c.to.toFixed(2)}`;

  // The exported run (if any) whose period holds this moment.
  function findRunFor(runs, ms) {
    if (!Array.isArray(runs) || !Number.isFinite(ms)) return null;
    return runs.find((r) => Number.isFinite(r.startMs) && Number.isFinite(r.endMs) && ms >= r.startMs && ms <= r.endMs) || null;
  }
  const findRunByKey = (runs, key) => (Array.isArray(runs) ? runs.find((r) => r.key === key) : null) || null;

  function fmtDay(ms) {
    return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  function warnText(run, what) {
    const when = run.lastExportedAtMs ? fmtDay(run.lastExportedAtMs) : 'earlier';
    return `Payroll for ${run.startYmd} to ${run.endYmd} was already exported (${when}${run.lastExportedBy ? ', by ' + run.lastExportedBy : ''}). `
      + `${what || 'This change'} will change hours that may already be paid. It is allowed, and it will be listed in the next Payroll Prep.`;
  }

  // ── session state (fetcher injected by clock.js) ──
  let _fetcher = null;
  let _keyFn = null;   // who/what the cache belongs to (tenant id) — a switch drops it
  let _key = null;
  let _runs = [];
  let _at = 0;
  let _inflight = null;
  // Did the LAST lookup for the current tenant succeed? false = the lock state is
  // UNKNOWN (lookup error, permission/claim mismatch after a tenant switch, an
  // answer discarded for another tenant, no fetcher). confirmEdit then still warns.
  let _lastOk = false;
  let _lastErr = null;

  const curKey = () => { try { return _keyFn ? _keyFn() : null; } catch (_) { return null; } };
  function setFetcher(fn, keyFn) { _fetcher = typeof fn === 'function' ? fn : null; _keyFn = typeof keyFn === 'function' ? keyFn : null; }
  function setRuns(runs) { _runs = Array.isArray(runs) ? runs : []; _at = Date.now(); _key = curKey(); _lastOk = true; _lastErr = null; }
  function getRuns() { return _runs; }
  function reset() { _runs = []; _at = 0; _inflight = null; _key = null; _lastOk = false; _lastErr = null; }
  const lookupOk = () => _lastOk && _key === curKey();

  // Never throws; a failed lookup leaves the last known runs in place.
  async function refresh(force) {
    if (!_fetcher) { _lastOk = false; return _runs; }
    // Another tenant/session than the one the cache was filled for: drop it (never serve or keep it).
    if (_key !== curKey()) { _runs = []; _at = 0; _inflight = null; _key = curKey(); _lastOk = false; }
    if (!force && _at && _lastOk && Date.now() - _at < CACHE_MS) return _runs;
    if (_inflight) return _inflight;
    const asked = curKey();
    _inflight = (async () => {
      try {
        const runs = await _fetcher();
        if (Array.isArray(runs) && curKey() === asked) setRuns(runs); // an answer for an earlier tenant is discarded
        else { _lastOk = false; _lastErr = 'answer for another company or not a list'; }
      } catch (e) {
        // Never blocks an edit (soft lock) — but the state is now UNKNOWN, and
        // confirmEdit warns on unknown (fail SAFE, integration re-review COD).
        _lastOk = false;
        _lastErr = (e && (e.code || e.message)) || String(e);
        try { console.warn('[PayrollRunLock] lookup failed:', e && e.message ? e.message : e); } catch (_) { /* no console */ }
      }
      return _runs;
    })();
    try { return await _inflight; } finally { _inflight = null; }
  }

  function unknownText(what) {
    return 'Could not check whether payroll for this day was already exported. If it was, '
      + `${what ? what.charAt(0).toLowerCase() + what.slice(1) : 'this change'} will change hours that may already be paid. `
      + 'It is allowed, and Payroll Prep lists any change to an exported period.';
  }
  // Best-effort trail for an edit made while the lock state was unknown.
  function _recordUnknown(entryMs, o, why) {
    try {
      const log = o.log || (typeof window !== 'undefined' ? window.logAudit : null);
      if (typeof log === 'function') {
        log('payroll', 'PAYROLL_LOCK_UNVERIFIED_EDIT', {
          source: 'payrollRunLock.confirmEdit', entryDay: Number.isFinite(entryMs) ? ymdOf(entryMs) : null,
          what: String(o.what || 'edit').slice(0, 120), reason: String(why || 'lookup failed').slice(0, 200),
        });
      }
    } catch (_) { /* recording is best-effort */ }
  }

  // Ask before an edit that lands in an exported period. Resolves true = go on.
  // SOFT lock: it warns, it never refuses. FAIL SAFE (integration re-review COD):
  // when the lock state cannot be determined (lookup error, a claim mismatch after a
  // tenant switch, an answer for another tenant) the editor is STILL warned that the
  // period may already be exported, and the go-ahead is recorded. Only a dialog that
  // cannot be shown at all lets the edit through silently (nothing else can be done).
  async function confirmEdit(entryMs, opts) {
    const o = opts || {};
    const ask = o.ask || (typeof window !== 'undefined' && typeof window.showConfirm === 'function' ? window.showConfirm : null);
    const decide = (ok) => (ok !== false && ok !== null && ok !== undefined ? !!ok : false);
    const askUnknown = async (why) => {
      if (!ask) return true;
      const ok = decide(await ask('Payroll may already be exported', unknownText(o.what), {
        confirmText: o.confirmText || 'Make the change', cancelText: 'Cancel', danger: false, safeDefault: true,
      }));
      if (ok) _recordUnknown(entryMs, o, why);
      return ok;
    };
    try {
      const runs = await refresh(false);
      const run = findRunFor(runs, entryMs);
      if (!run) return lookupOk() ? true : await askUnknown(_lastErr);
      if (!ask) return true;
      return decide(await ask('Payroll already exported for this period', warnText(run, o.what), {
        confirmText: o.confirmText || 'Make the change', cancelText: 'Cancel', danger: false, safeDefault: true,
      }));
    } catch (e) {
      try { return await askUnknown(e && (e.code || e.message)); } catch (_) { return true; }
    }
  }

  // TASK-9443 slice 2 — one question for an edit that touches SEVERAL shifts
  // (billing Review moving several EMPLOYEE lines at once). msList = every
  // clock-in the edit touches (old and new). Asks ONCE, naming every exported
  // period hit. Same soft-lock contract as confirmEdit: never refuses on its
  // own, warns on an unknown lock state, resolves true = go on.
  function periodsText(runs) {
    return runs.map((r) => `${r.startYmd} to ${r.endYmd}`).join(', ');
  }
  async function confirmEditMany(msList, opts) {
    const o = opts || {};
    const list = (Array.isArray(msList) ? msList : []).filter((m) => Number.isFinite(m));
    if (!list.length) return true;
    const ask = o.ask || (typeof window !== 'undefined' && typeof window.showConfirm === 'function' ? window.showConfirm : null);
    try {
      const runs = await refresh(false);
      const hit = [];
      list.forEach((ms) => { const r = findRunFor(runs, ms); if (r && !hit.some((h) => h.key === r.key)) hit.push(r); });
      if (!hit.length) {
        if (lookupOk()) return true;
        return await confirmEdit(list[0], o); // unknown state: confirmEdit's "may already be exported" path
      }
      if (hit.length === 1) return await confirmEdit(list.find((ms) => findRunFor(runs, ms) === hit[0]), o);
      if (!ask) return true;
      const ok = await ask('Payroll already exported for these periods',
        `Payroll for ${periodsText(hit)} was already exported. ${o.what || 'This change'} will change hours that may already be paid. `
        + 'It is allowed, and it will be listed in the next Payroll Prep.',
        { confirmText: o.confirmText || 'Make the change', cancelText: 'Cancel', danger: false, safeDefault: true });
      return ok !== false && ok !== null && ok !== undefined ? !!ok : false;
    } catch (_) {
      try { return await confirmEdit(list[0], o); } catch (__) { return true; }
    }
  }

  // TASK-9443 slice 3 — "Payroll exported" marker on the Workforce day list.
  // DISPLAY ONLY: it never asks, never refuses, never writes. The exported run
  // holding ANY of these clock-in moments, or null. The caller shows nothing
  // when the lookup state is unknown (lookupOk() false): a missing marker is
  // not a promise, the edit warning (confirmEdit) still asks on unknown.
  function exportedRunForAny(runs, msList) {
    const list = (Array.isArray(msList) ? msList : []).filter((m) => Number.isFinite(m));
    for (const ms of list) { const r = findRunFor(runs, ms); if (r) return r; }
    return null;
  }
  function exportedChipText(run) {
    if (!run) return null;
    const when = Number.isFinite(run.lastExportedAtMs) && run.lastExportedAtMs > 0 ? fmtDay(run.lastExportedAtMs) : '';
    return {
      label: when ? `Payroll exported ${when}` : 'Payroll exported',
      title: `Payroll for ${run.startYmd} to ${run.endYmd} was already exported${when ? ' (' + when + ')' : ''}. `
        + 'Edits are still allowed; Payroll Prep lists any change to hours in this period.',
    };
  }
  // Changes when the set of exported periods (or an export time) changes, so a
  // view repaints only when what it would show moved.
  const runsFingerprint = (runs) => (Array.isArray(runs) ? runs : [])
    .map((r) => `${r && r.key}|${r && r.startMs}|${r && r.endMs}|${r && r.lastExportedAtMs}`).sort().join(',');

  return { FIELDS, snapshotRows, diffRows, describe, findRunFor, findRunByKey, warnText, unknownText, ymdOf,
    setFetcher, setRuns, getRuns, refresh, reset, confirmEdit, confirmEditMany, lookupOk,
    exportedRunForAny, exportedChipText, runsFingerprint };
});
